<!--
This component is a button made with 2 parts:
- a container with a centered icon
- a label with text

The button can be customized:
- 'size': 'inline' or 'full-width'
- 'icon': a Svelte component for the icon
- 'label': text to display in the button
- 'backgroundColor': color of the button background
- 'textColor': color of the text in the button
- 'hasShadow': boolean to add a shadow effect

The button is responsive:
- on desktop, the height is 40px
- on mobile, the height is 56px
- the icon and label are centered vertically and horizontally
-->

<script>
    import "../../app.css";

    // Props per personalizzazione
    export let icon = null;
    export let label = '';
    export let size = 'inline'; // 'inline' | 'full-width'
    export let backgroundColor = 'var(--orange)';
    export let textColor = 'white';
    export let hasShadow = false;
    export let type = 'button'; // 'button' | 'submit' | 'reset'
    export let disabled = false;
    export let href = null; // Se passato, renderizza come link
    export let target = null;
    export let ariaLabel = null;
    export let onclick = null;
    export let reload = false; // Se true, apre in una nuova scheda
    export let loading = false; // Stato di caricamento

    
    // Computed classes
    $: buttonClasses = [
        'button',
        size === 'full-width' ? 'full-width' : 'inline',
        hasShadow ? 'shadow' : '',
        disabled ? 'disabled' : '',
        !icon ? 'text-only' : '',
        !label ? 'icon-only' : ''
    ].filter(Boolean).join(' ');
    
    // Event handler
    function handleClick(event) {
        if (disabled) {
            event.preventDefault();
            return;
        }
        if (onclick) {
            onclick(event);
        }
    }
    
    function handleKeydown(event) {
        if (disabled) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleClick(event);
        }
    }
</script>

<!-- Conditional rendering: link vs button -->
{#if href && !disabled}
    <a 
        {href}
        {target}
        class={buttonClasses}
        aria-label={ariaLabel || label}
        role="button"
        on:click={handleClick}
        on:keydown={handleKeydown}
        style="--bg-color: {backgroundColor}; --text-color: {textColor}; color: {textColor};"
        tabindex="0"
        data-sveltekit-reload={reload ? "true" : undefined}
    >
        {#if icon}
            <div class="icon" aria-hidden="true">
                <svelte:component this={icon}/>
            </div>
        {/if}
        {#if label}
            <div class="label">
                <span>{label}</span>
            </div>
        {/if}
        <slot />
    </a>
{:else}
    <button 
        {type}
        {disabled}
        class={buttonClasses}
        aria-label={ariaLabel || label}
        on:click={handleClick}
        style="--bg-color: {backgroundColor}; --text-color: {textColor};"
    >
        {#if loading}
            <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
        {:else}
            {#if icon}
                <div class="icon" aria-hidden="true">
                    <svelte:component this={icon} style="color: {textColor};"/>
                </div>
            {/if}
            {#if label}
                <div class="label">
                    <span>{label}</span>
                </div>
            {/if}
        {/if}
        <slot />
    </button>
{/if}

<style>
    /* Base button styles */
    .button {
        display: inline-flex;
        align-items: center;
        border: none;
        cursor: pointer;
        text-decoration: none;
        font-family: "Inter", sans-serif;
        transition: all 0.2s ease;
        position: relative;
        background: none;
        outline: none;
        user-select: none;
        vertical-align: middle;
    }

    /* Size variants */
    .button.inline {
        width: auto;
    }

    .button.full-width {
        width: 100%;
    }

    /* Shadow effect */
    .button.shadow {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .button.shadow:hover:not(.disabled) {
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
        transform: translateY(-1px);
    }

    .button.shadow:active:not(.disabled) {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        transform: translateY(0);
    }

    /* Disabled state */
    .button.disabled {
        cursor: not-allowed;
        opacity: 0.6;
        pointer-events: none;
    }

    /* Hover effects */
    .button:hover:not(.disabled) {
        filter: brightness(1.1);
    }

    .button:active:not(.disabled) {
        filter: brightness(0.9);
        transform: scale(0.98);
    }

    /* Focus styles for accessibility */
    .button:focus-visible {
        outline: 2px solid #007bff;
        outline-offset: 2px;
    }

    /* Icon container */
    .icon {
        background-color: var(--bg-color, var(--orange));
        width: var(--button-height-desktop, 40px);
        height: var(--button-height-desktop, 40px);
        border-radius: 16px 4px 4px 16px;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 10px;
        box-sizing: border-box;
        flex-shrink: 0;
    }

    /* Label container */
    .label {
        background-color: var(--bg-color, var(--orange));
        margin-left: 2px;
        border-radius: 4px 16px 16px 4px;
        height: var(--button-height-desktop, 40px);
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0 var(--button-h-padding, 16px);
        flex-grow: 1;
    }

    .label span {
        font-weight: var(--button-font-weight, 500);
        font-family: "Inter", sans-serif;
        font-size: var(--button-font-size, 14px);
        color: var(--text-color, white);
        white-space: nowrap;
        line-height: 1;
    }

    /* Text-only button (no icon) */
    .button.text-only .label {
        border-radius: 16px;
        margin-left: 0;
    }

    /* Icon-only button (no text) */
    .button.icon-only .icon {
        border-radius: 16px;
    }

    /* Mobile responsive */
    @media (max-width: 768px) {
        .icon {
            width: var(--button-height-mobile, 40px);
            height: var(--button-height-mobile, 40px);
            padding: 14px;
        }

        .label {
            height: var(--button-height-mobile, 40px);
            padding: 8px 12px;
        }

        .label span {
            font-size: var(--button-font-size-mobile, 16px);
        }
    }

    /* High contrast mode support */
    @media (prefers-contrast: high) {
        .button {
            border: 2px solid var(--text-color, white);
        }
    }

    /* Reduced motion support */
    @media (prefers-reduced-motion: reduce) {
        .button {
            transition: none;
        }
        
        .button.shadow:hover:not(.disabled) {
            transform: none;
        }
        
        .button:active:not(.disabled) {
            transform: none;
        }
    }

    /* Dark mode support */
    @media (prefers-color-scheme: dark) {
        .button:focus-visible {
            outline-color: #4dabf7;
        }
    }
</style>
